// 智能拼豆板 BLE 接入
// 首选 BeadLink 开放协议（本仓库发起，SPEC 见 /home/ubuntu/BeadLink），
// 兼容 pixdot 逆向协议（像素糖果 HPD 系列，PDD_ 前缀设备）。
//
// ⚠️ pixdot 兼容路径的 service UUID 未经真机验证；BeadLink 路径 UUID 为协议正式分配。
//
// Copyright 2026 豆田造物 —— BeadLink 部分按 Apache-2.0 开放

// ===================== BeadLink 协议（自有开放协议） =====================

const BL_SERVICE_UUID = '2369abf5-5028-48fc-a6e3-8f28235485ff'
const BL_CMD_UUID = '2369abf5-5028-48fc-a6e3-8f282354f101'
const BL_EVENT_UUID = '2369abf5-5028-48fc-a6e3-8f282354f102'

const BL = {
  MAGIC: 0xbd,
  VERSION: 0x01,
  CLEAR: 0x10,
  SET_BRIGHTNESS: 0x11,
  SET_PALETTE: 0x13,
  DRAW_PIXELS: 0x20,
  ACK: 0x80,
  CAPABILITY_REPORT: 0x81,
  NACK: 0x82,
}

function blCrc8(bytes) {
  let crc = 0
  for (const byte of bytes) {
    crc ^= byte
    for (let i = 0; i < 8; i++) {
      crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff
    }
  }
  return crc
}

function blFrame(command, payload = []) {
  const body = [BL.MAGIC, BL.VERSION, command, payload.length & 0xff, (payload.length >> 8) & 0xff, ...payload]
  return new Uint8Array([...body, blCrc8(body)])
}

// ===================== pixdot 协议（兼容存量设备） =====================

const PIXDOT_NAME_PREFIX = 'PDD_'
const PIXDOT_SERVICE_UUIDS = [
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000fee7-0000-1000-8000-00805f9b34fb',
  '0000ffe5-0000-1000-8000-00805f9b34fb',
]
const MAX_WRITE_SIZE = 509
const BOARD_SIDE_LENGTHS = [32, 52, 78, 104]

function pixdotFrame(bytes) {
  const size = bytes.length + 2
  return new Uint8Array([size & 0xff, (size >> 8) & 0xff, ...bytes])
}

function hexToRgbArr(hex) {
  const n = parseInt(String(hex).replace('#', ''), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function isBluetoothSupported() {
  return typeof navigator !== 'undefined' && Boolean(navigator.bluetooth)
}

export class BeadBoard {
  constructor() {
    this.device = null
    this.writeChar = null
    this.notifyChar = null
    this.protocol = null // 'beadlink' | 'pixdot'
    this.sideLength = null
    this.onDisconnect = null
    this._blWaiter = null
  }

  get connected() {
    return Boolean(this.device?.gatt?.connected && this.writeChar)
  }

  async connect() {
    if (!isBluetoothSupported()) {
      throw new Error('当前浏览器不支持蓝牙（请使用 Chrome/Edge 浏览器）')
    }
    // 同时扫描 BeadLink 服务与 pixdot 设备名前缀
    const device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [BL_SERVICE_UUID] }, { namePrefix: PIXDOT_NAME_PREFIX }],
      optionalServices: [BL_SERVICE_UUID, ...PIXDOT_SERVICE_UUIDS],
    })
    const server = await device.gatt.connect()

    // 优先 BeadLink 协议
    try {
      const service = await server.getPrimaryService(BL_SERVICE_UUID)
      this.writeChar = await service.getCharacteristic(BL_CMD_UUID)
      this.notifyChar = await service.getCharacteristic(BL_EVENT_UUID)
      this.protocol = 'beadlink'
    } catch {
      // 回退 pixdot 候选服务
      let lastError = null
      for (const uuid of PIXDOT_SERVICE_UUIDS) {
        try {
          const service = await server.getPrimaryService(uuid)
          const chars = await service.getCharacteristics()
          const writeChar = chars.find((c) => c.properties.writeWithoutResponse) || chars.find((c) => c.properties.write)
          const notifyChar = chars.find((c) => c.properties.notify || c.properties.indicate)
          if (!writeChar) throw new Error('服务内无可写特征')
          this.writeChar = writeChar
          this.notifyChar = notifyChar || null
          this.protocol = 'pixdot'
          break
        } catch (err) {
          lastError = err
        }
      }
      if (!this.writeChar) {
        device.gatt.disconnect()
        throw lastError || new Error('未找到拼豆板服务')
      }
    }

    this.device = device
    device.addEventListener('gattserverdisconnected', () => {
      this.writeChar = null
      this.onDisconnect?.()
    })
    if (this.notifyChar) {
      await this.notifyChar.startNotifications()
      this.notifyChar.addEventListener('characteristicvaluechanged', (e) => this._onNotify(e))
    }

    if (this.protocol === 'beadlink') {
      await this._blNegotiate()
    } else {
      // pixdot：查询板子尺寸（08 00 01 80 HH MM SS 00）
      const now = new Date()
      await this._write(pixdotFrame([0x01, 0x80, now.getHours(), now.getMinutes(), now.getSeconds(), 0x00]))
        .catch(() => {})
    }
    return this
  }

  async _write(frame) {
    if (!this.connected) throw new Error('拼豆板未连接')
    for (let offset = 0; offset < frame.length; offset += MAX_WRITE_SIZE) {
      const chunk = frame.slice(offset, offset + MAX_WRITE_SIZE)
      if (this.writeChar.properties.writeWithoutResponse) {
        await this.writeChar.writeValueWithoutResponse(chunk)
      } else {
        await this.writeChar.writeValueWithResponse(chunk)
      }
    }
  }

  _onNotify(e) {
    const value = e.target.value
    if (!value) return
    const data = new Uint8Array(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength))
    if (this.protocol === 'beadlink') {
      if (data.length < 6 || data[0] !== BL.MAGIC) return
      const cmd = data[2]
      const payloadLen = data[3] | (data[4] << 8)
      const payload = data.slice(5, 5 + payloadLen)
      if (cmd === BL.CAPABILITY_REPORT) {
        this.sideLength = payload[1] | (payload[2] << 8) // 宽（方形板宽=高）
        if (this._blWaiter) {
          clearTimeout(this._blWaiter.timer)
          this._blWaiter.resolve(payload)
          this._blWaiter = null
        }
      } else if (cmd === BL.NACK) {
        console.warn('[beadlink] NACK:', payload[0], payload[1])
      }
    } else {
      // pixdot：通知 0x8001 第 4 字节为尺寸枚举
      if (data.length >= 5 && data[0] === 0x01 && data[1] === 0x80) {
        this.sideLength = BOARD_SIDE_LENGTHS[data[4]] || null
      }
    }
  }

  async _blNegotiate() {
    const waiter = new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), 2000)
      this._blWaiter = { resolve, timer }
    })
    await this._write(blFrame(0x01, [BL.VERSION])) // CAPABILITY_QUERY
    await waiter
  }

  async clear() {
    if (this.protocol === 'beadlink') await this._write(blFrame(BL.CLEAR))
    else await this._write(new Uint8Array([0x04, 0x00, 0x03, 0x80]))
  }

  async setBrightness(percent) {
    const p = Math.max(0, Math.min(100, Math.round(percent)))
    if (this.protocol === 'beadlink') await this._write(blFrame(BL.SET_BRIGHTNESS, [p]))
    else await this._write(new Uint8Array([0x05, 0x00, 0x04, 0x80, p]))
  }

  // 同步某个色号到拼豆板：清屏 → 点亮该色号全部格子（超出板子范围的坐标丢弃）
  async syncColor(hex, cells) {
    if (!this.connected) return { sent: 0, dropped: 0 }
    const side = this.sideLength
    let dropped = 0
    const valid = []
    for (const cell of cells) {
      if (side && (cell.x >= side || cell.y >= side)) dropped++
      else valid.push(cell)
    }
    await this.clear()
    if (valid.length === 0) return { sent: 0, dropped }

    if (this.protocol === 'beadlink') {
      const [r, g, b] = hexToRgbArr(hex)
      await this._write(blFrame(BL.SET_PALETTE, [0, 1, r, g, b]))
      const maxPayload = 509 - 6
      const perFrame = Math.floor((maxPayload - 3) / 4)
      for (let offset = 0; offset < valid.length; offset += perFrame) {
        const chunk = valid.slice(offset, offset + perFrame)
        const payload = [0, chunk.length & 0xff, (chunk.length >> 8) & 0xff]
        for (const c of chunk) payload.push(c.x & 0xff, (c.x >> 8) & 0xff, c.y & 0xff, (c.y >> 8) & 0xff)
        await this._write(blFrame(BL.DRAW_PIXELS, payload))
      }
    } else {
      const [r, g, b] = hexToRgbArr(hex)
      const perFrame = 6000
      for (let offset = 0; offset < valid.length; offset += perFrame) {
        const chunk = valid.slice(offset, offset + perFrame)
        const body = [0x05, 0x01, offset === 0 ? 0x00 : 0x02, r, g, b]
        for (const c of chunk) body.push(c.x & 0xff, c.y & 0xff)
        await this._write(pixdotFrame(body))
      }
    }
    return { sent: valid.length, dropped }
  }

  disconnect() {
    this.device?.gatt?.disconnect()
    this.writeChar = null
  }
}
